import {
  parseCampaignAttribution,
  type CampaignSearchParams,
} from "@/lib/campaign-attribution";
import { sanitiseSignupSource } from "@/lib/signup-next";
import { isUuid } from "@/lib/uuid";

/**
 * A person signed in with a personal (candidate) account cannot also hold an
 * employer membership yet: `getUserRole()` returns `candidate` whenever a
 * profile exists. Until dual-role accounts exist, hiring routes show a
 * "sign out and continue" screen instead of silently redirecting to
 * Companion, and the employer signup form explains duplicate emails.
 */
export type SignOutContinueTarget = "employer-signup" | "employer-login";

type ParamValue = string | string[] | null | undefined;

/**
 * Query that must survive the sign-out-and-continue step: a team invite (so
 * the person joins the inviting company instead of creating a duplicate one)
 * and the first-party source and UTM attribution of the hiring visit.
 */
export type HiringContinueParams = {
  invite?: ParamValue;
  source?: ParamValue;
} & CampaignSearchParams;

function firstValue(value: ParamValue): string | null {
  if (Array.isArray(value)) return value[0] ?? null;
  return value ?? null;
}

/** Only the known, sanitised continue parameters, as plain strings. */
export function sanitiseHiringContinueParams(
  params: HiringContinueParams | null | undefined,
): Record<string, string> {
  if (!params) return {};
  const output: Record<string, string> = {};
  const invite = firstValue(params.invite)?.trim().toLowerCase();
  if (isUuid(invite)) output.invite = invite;
  const source = sanitiseSignupSource(params.source);
  if (source) output.source = source;
  Object.assign(output, parseCampaignAttribution(params) ?? {});
  return output;
}

/** Employer signup, keeping a valid invite, the source and UTMs. */
export function employerSignupPath(params?: HiringContinueParams | null): string {
  const query = new URLSearchParams(sanitiseHiringContinueParams(params));
  return query.size > 0 ? `/employer-signup?${query}` : "/employer-signup";
}

const EMPLOYER_LOGIN_CONTINUE_PATH = `/login?${new URLSearchParams({ redirect: "/employer/dashboard" })}`;

/** Where the browser goes after signing the personal account out. */
export function signOutContinuePath(
  target: SignOutContinueTarget,
  params?: HiringContinueParams | null,
): string {
  return target === "employer-signup"
    ? employerSignupPath(params)
    : EMPLOYER_LOGIN_CONTINUE_PATH;
}

/** Hiring routes send a signed-in personal account here, never to Companion. */
export const PERSONAL_ACCOUNT_HIRING_PATH = "/employer-signup";

/** Employer login that lands on the employer dashboard afterwards. */
export const EMPLOYER_LOGIN_PATH = EMPLOYER_LOGIN_CONTINUE_PATH;
